<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'Settings'">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'UserAccount' })"
        />
      </template>
    </MainHeader>

    <div class="view settings-view">
      <!-- Tab switcher -->
      <div class="et-tabs">
        <button
          class="et-tab"
          :class="{ active: !editTransactionSliderSelected }"
          @click="editTransactionSliderSelected = false"
        >
          Info
        </button>
        <button
          class="et-tab"
          :class="{ active: editTransactionSliderSelected }"
          @click="editTransactionSliderSelected = true"
        >
          Edit
        </button>
      </div>

      <!-- ── INFO TAB ── -->
      <div v-if="!editTransactionSliderSelected">
        <div class="wrapper-card settings-info-card">
          <div class="ud-section">
            <span class="section-label">Display</span>
            <div class="et-info-row">
              <span class="et-info-label">Dark Mode</span>
              <span
                class="et-flag"
                :class="user.settings.darkMode ? 'flag-on' : 'flag-off'"
              >
                {{ user.settings.darkMode ? 'On' : 'Off' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── EDIT TAB ── -->
      <div v-else>
        <form @submit.prevent="UpdateUserSettings">
          <fieldset>
            <div class="ud-section">
              <span class="section-label">Display</span>
              <div class="toggle-row">
                <span class="ud-label">Dark Mode</span>
                <Toggle
                  id="darkMode"
                  :toggleActive="darkMode"
                  @toggleSelected="toggleDarkMode($event)"
                />
              </div>
            </div>

            <button
              role="button"
              type="submit"
              aria-label="Save settings"
              class="ud-submit"
            >
              Save Settings
            </button>
          </fieldset>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../../store.js';
import { useRouter } from 'vue-router';
import { ref, onMounted, computed } from 'vue';
import MainHeader from '../../components/View-Header.vue';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import Toggle from '../../components/Toggle.vue';

export default {
  components: { MainHeader, SkeletonLoader, Toggle },
  setup() {
    const store = useMyStore();
    const router = useRouter();

    let editTransactionSliderSelected = ref(false);
    const user = computed(() => store.user);
    const loading = ref(true);
    let loadingText = ref('Loading...');
    const darkMode = ref(user.value.settings?.darkMode || false);

    const UpdateUserSettings = async () => {
      loadingText.value = 'Saving Settings...';
      loading.value = true;
      try {
        let userSettings = { darkMode: darkMode.value };
        store.updateUserSettings(userSettings).then((response) => {
          if (response.status === 200) {
            editTransactionSliderSelected.value = false;
            loading.value = false;
          }
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    const toggleDarkMode = (value) => {
      darkMode.value = value;
    };

    onMounted(async () => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);
      store.getUserAccountData().then(() => {
        loading.value = false;
      });
    });

    return {
      store,
      router,
      editTransactionSliderSelected,
      user,
      loading,
      loadingText,
      UpdateUserSettings,
      darkMode,
      toggleDarkMode,
    };
  },
};
</script>

<style scoped>
.settings-view {
  max-width: 480px;
  margin: 0 auto;
}

.et-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 1.5rem;
  background: #f0f0f4;
  border-radius: var(--radius-pill);
  padding: 4px;
}
.dark-theme .et-tabs {
  background: #1a2535;
}

.et-tab {
  border: none;
  border-radius: var(--radius-pill);
  padding: 0.6rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: #888;
  transition: all 0.2s ease;
}
.et-tab.active {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  box-shadow: 0 4px 12px rgba(127, 119, 221, 0.3);
}

.settings-info-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}

.et-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .et-info-row {
  border-bottom-color: #2a3a4e;
}
.et-info-row:last-child {
  border-bottom: none;
}
.et-info-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #888;
}

.et-flag {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'Settings'">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'UserAccount' })"
        />
      </template>
    </MainHeader>
    <div class="settings-view">
      <div class="view">
        <div>
          <Slider
            :options="sliderOptions"
            @sliderSelection="sliderOptionUpdated($event)"
          ></Slider>
        </div>
        <div v-if="!editTransactionSliderSelected">
          <div class="text-center pt-4 pb-4">
            <font-awesome-icon
              :icon="['fas', 'circle-user']"
              class="info-icon view-icon"
            />
          </div>
          <div class="user-info-card">
            <div>
              <label class="small-grey-text">Dark Mode</label>
              <p id="transfer">
                <font-awesome-icon
                  :icon="
                    user.settings.darkMode === true
                      ? ['fas', 'circle-check']
                      : ['fas', 'circle-xmark']
                  "
                  class="teal"
                />
              </p>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="text-center pt-4 pb-4">
            <font-awesome-icon
              :icon="['fas', 'file-pen']"
              class="view-icon edit-icon"
            />
          </div>
          <form @submit.prevent="UpdateUserSettings">
            <fieldset>
              <div>
                <label class="small-grey-text">Dark Mode</label>
                <Toggle
                  id="darkMode"
                  :toggleActive="darkMode"
                  @toggleSelected="toggleDarkMode($event)"
                />
              </div>
              <button
                role="button"
                type="submit"
                aria-label="submit update user profile"
                class="blue-button mt-4 w-100"
              >
                Submit
              </button>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../../store.js';
import { useRouter } from 'vue-router';
import { ref, onMounted, computed } from 'vue';

import MainHeader from '../../components/View-Header.vue';
import Slider from '../../components/Slider.vue';
import { Edit_Transaction_Types } from '../../utils/constants.js';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import Toggle from '../../components/Toggle.vue';

export default {
  components: { MainHeader, Slider, SkeletonLoader, Toggle },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const sliderOptions = [
      { label: 'Info', disabled: false },
      { label: 'Edit', disabled: true },
    ];
    let editTransactionSliderSelected = ref(false);
    const user = computed(() => store.user);
    const loading = ref(true);
    let loadingText = ref('Loading...');
    const darkMode = ref(user.value.settings?.darkMode || false);

    const sliderOptionUpdated = (selection) => {
      if (selection === Edit_Transaction_Types.edit) {
        editTransactionSliderSelected.value = true;
      } else {
        editTransactionSliderSelected.value = false;
      }
    };

    const UpdateUserSettings = async () => {
      loadingText.value = 'Saving Settings...';
      loading.value = true;

      try {
        let userSettings = {
          darkMode: darkMode.value,
        };

        store.updateUserSettings(userSettings).then((response) => {
          if (response.status === 200) {
            editTransactionSliderSelected.value = false;
            loading.value = false;
          }
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    const toggleDarkMode = (value) => {
      darkMode.value = value;
    };

    onMounted(async () => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);
      store.getUserAccountData().then(() => {
        loading.value = false;
      });
    });

    return {
      store,
      router,
      sliderOptions,
      sliderOptionUpdated,
      editTransactionSliderSelected,
      user,
      loading,
      loadingText,
      UpdateUserSettings,
      darkMode,
      toggleDarkMode,
    };
  },
};
</script>

<style scoped>
.info-icon,
.edit-icon {
  color: #e8f44a;
}

.user-info-card {
  padding: 1em;
  border-radius: 5px;
  box-shadow: 0px 20px 20px rgba(0, 0, 0, 0.1);
}

.user-info-card p {
  margin: 0;
}

.light-theme .user-info-card {
  background: #f8f8f8;
}

.dark-theme .user-info-card {
  background: #212e3e;
  color: white;
}

.settings-view .input {
  width: 100%;
  box-sizing: border-box;
  font-size: 1em;
}
</style> -->
